// Per-tab persistence of the Explorer map's pan/zoom state. Used by
// MapView to capture the user's regional context (e.g. a cluster-zoomed
// view of the West Coast mills) so that navigating Back from DetailView
// restores that exact view instead of zooming all the way in to the
// just-selected mill.
//
// Why sessionStorage and not localStorage:
// - Per-tab — multiple tabs don't compete for the same "last view".
// - Cleared when the tab closes — a returning visitor in a new tab
//   gets the default CONUS-fit view, not whatever their previous
//   session was last looking at.
// - The view is transient by nature; localStorage's persistence
//   semantics aren't useful here.

const STORAGE_KEY = 'sfi.explorer.mapView';

export interface SavedExplorerMapView {
  lat: number;
  lng: number;
  zoom: number;
}

const isSavedView = (raw: unknown): raw is SavedExplorerMapView =>
  typeof raw === 'object' &&
  raw !== null &&
  typeof (raw as SavedExplorerMapView).lat === 'number' &&
  typeof (raw as SavedExplorerMapView).lng === 'number' &&
  typeof (raw as SavedExplorerMapView).zoom === 'number' &&
  Number.isFinite((raw as SavedExplorerMapView).lat) &&
  Number.isFinite((raw as SavedExplorerMapView).lng) &&
  Number.isFinite((raw as SavedExplorerMapView).zoom);

export const readSavedExplorerMapView = (): SavedExplorerMapView | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isSavedView(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeSavedExplorerMapView = (view: SavedExplorerMapView): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(view));
  } catch {
    // ignore quota / privacy-mode failures — in-memory state is unaffected
  }
};

export const clearSavedExplorerMapView = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
